import _jsx from "@babel/runtime/helpers/jsx";
var _HotCar, _NewlyListed, _ReducedPrice, _BeyondRadius, _SimilarMatch, _Label;
import React, { isValidElement } from 'react';
import PropTypes from 'prop-types';
import { useBrand } from '@atc/brand';
import { Label } from 'reaxl';
import { BeyondRadius, HotCar, NewlyListed, ReducedPrice, SimilarMatch } from 'reaxl-molecules';
import clsx from 'clsx';
import { useFeatures } from '@atc/features';
import InventoryListingSaveGlyph from '../InventoryListingSaveGlyph/index.js';

/*
 * InventoryListingOverlayActions is a layout component that handles the overlay actions
 * positioned over the inventory listing image.
 * For performance reasons this functionality is positioned relatively to the image via CSS
 * rather than using the PositionOverlay component.
 *
 * This component renders:
 * - Hot Car, Newly Listed, Reduced Price badges
 * - Similar Match and Beyond Radius badges
 * - 360° tour indicator
 * - Save/compare functionality
 *
 * LANDSCAPE:
 * ,------------------- Image-- -
 * |  ---                  ---  |
 * | |Icon|               |Icon||
 * |  ---                  ---  |
 * |                            |
 * |                            |
 *  ----------------------------
 *
 * PORTRAIT:
 * ,----_--- Image-- -
 * |  ---       ---  |
 * | |Icon|    |Icon||
 * |  ---       ---  |
 * |                 |
 * |                 |
 * |                 |
 * |                 |
 *  -----------------
 */
function InventoryListingOverlayActions(_ref) {
  let {
    onSaveToggle,
    listing,
    isSaved,
    show360,
    additionalCta,
    uiContext = 'default'
  } = _ref;
  const {
    isBrand
  } = useBrand();
  const isFord = isBrand('ford');
  const {
    compare: [, {
      disableSaveIcon
    }]
  } = useFeatures(['compare']);
  const {
    isNewlyListed,
    isReducedPrice,
    isHot,
    isSupplemental,
    isSimilarListing
  } = listing || {};
  const hasAdditionalCta = /*#__PURE__*/isValidElement(additionalCta);
  const isSnapshot = uiContext === 'snapshot';
  const badges = [];
  const badgesClassName = clsx('overlay-glyphs col-xs-10 col-sm-9 col-md-10 display-flex flex-wrap gap-1 align-items-start', {
    'padding-left-1 padding-right-0': isSnapshot
  });
  const iconClassName = clsx('overlay-glyphs display-flex align-items-center justify-content-end', {
    'padding-left-0 padding-right-1': isSnapshot,
    'col-xs-2 col-sm-3 col-md-2': !hasAdditionalCta,
    'col-xs-12 margin-top-2 padding-right-4': hasAdditionalCta
  });
  if (!isFord) {
    // Prioritize and only show HotCar, NewlyListed, **or** ReducedPrice badge
    if (isHot && uiContext !== 'snapshot') {
      badges.push(_HotCar || (_HotCar = /*#__PURE__*/_jsx(HotCar, {
        bsStyle: "inverse"
      }, "hot-car")));
    }
    if (!badges.length && isNewlyListed) {
      badges.push(_NewlyListed || (_NewlyListed = /*#__PURE__*/_jsx(NewlyListed, {
        bsStyle: "inverse"
      }, "newly-listed")));
    }
    if (!badges.length && isReducedPrice) {
      badges.push(_ReducedPrice || (_ReducedPrice = /*#__PURE__*/_jsx(ReducedPrice, {
        bsStyle: "inverse",
        label: "Price Drop"
      }, "reduced-price")));
    }
    // List Beyond Radius or Similar Match badge **first** if applicable
    if (isSupplemental) {
      badges.unshift(_BeyondRadius || (_BeyondRadius = /*#__PURE__*/_jsx(BeyondRadius, {
        bsStyle: "inverse"
      }, "beyond-radius")));
    }
    if (isSimilarListing) {
      badges.unshift(_SimilarMatch || (_SimilarMatch = /*#__PURE__*/_jsx(SimilarMatch, {
        bsStyle: "inverse"
      }, "similar-match")));
    }
  }
  // 360 badge can be shown for Ford
  if (show360) {
    badges.push(_Label || (_Label = /*#__PURE__*/_jsx(Label, {
      bsStyle: "inverse"
    }, "tour-360", "360\xB0")));
  }
  return /*#__PURE__*/React.createElement(React.Fragment, null, !hasAdditionalCta && /*#__PURE__*/_jsx("div", {
    className: badgesClassName
  }, void 0, badges), /*#__PURE__*/_jsx("div", {
    className: iconClassName
  }, void 0, hasAdditionalCta && additionalCta, !disableSaveIcon && onSaveToggle && /*#__PURE__*/_jsx(InventoryListingSaveGlyph, {
    listing: listing,
    onSaveToggle: onSaveToggle,
    uiContext: "accent-circle",
    isSaved: isSaved
  })));
}
export default InventoryListingOverlayActions;